import * as React from 'react'
import { Link, useSearchParams } from 'react-router'
import { BadgeCheck, Coins } from 'lucide-react'
import type { BillingEntryDto, BillingSummaryDto } from '@lib/dto/billing.dto'
import { pageCursorSchema } from '@lib/helpers/page-cursor'
import { formatMicros, formatPrice } from '@lib/helpers/money'
import { uuidParamSchema } from '@lib/validators/params'
import { AutoReloadCard } from '@/components/billing/AutoReloadCard'
import { AutoReloadDialog } from '@/components/billing/AutoReloadDialog'
import { BillingEntryDetail, hasEntryDetail, REFUND_STATUS } from '@/components/billing/BillingEntryDetail'
import { BuyCreditDialog } from '@/components/billing/BuyCreditDialog'
import { CardSetupReturn } from '@/components/billing/CardSetupReturn'
import { CheckoutReturn } from '@/components/billing/CheckoutReturn'
import { RefundDialog } from '@/components/billing/RefundDialog'
import { ThresholdDialog } from '@/components/billing/ThresholdDialog'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { RecordSheet } from '@/components/RecordSheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { canManageBilling, useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useBillingHistory, useBillingSummary } from '@/hooks/useBilling'
import { cn } from '@/lib/utils'

const ENTRY_LABELS: Record<BillingEntryDto['type'], string> = {
  send_charge: 'Live envelope',
  top_up: 'Credit purchase',
  auto_reload: 'Auto-reload',
  grant: 'Credit from PixelSign',
  refund: 'Refund',
  closure_refund: 'Refund on closing',
  closure_forfeit: 'Granted credit removed on closing',
}

/** What an entry was, in words, without links. */
function entryText(entry: BillingEntryDto): string {
  if (entry.type === 'grant' && entry.grantReason) return `${ENTRY_LABELS.grant}: ${entry.grantReason}`
  if (entry.type === 'top_up' && entry.purchase?.source === 'wallet') return `${ENTRY_LABELS.top_up} (wallet)`
  return ENTRY_LABELS[entry.type]
}

/** A refund still on its way to the cards, or one PixelSign has to follow up. */
function RefundBadge({ entry }: { entry: BillingEntryDto }) {
  if (!entry.refund || entry.refund.status === 'succeeded') return null
  const status = REFUND_STATUS[entry.refund.status]
  return <Badge tone={status.tone}>{status.label}</Badge>
}

interface EntryDescriptionProps {
  entry: BillingEntryDto
  basePath: string
  /** Opens the entry's details, for a purchase or a refund. */
  onOpen: () => void
}

/** What an entry was, in words: a charge links to its envelope, a purchase or refund opens its details. */
function EntryDescription({ entry, basePath, onOpen }: EntryDescriptionProps) {
  if (entry.type === 'send_charge') {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        {ENTRY_LABELS.send_charge}
        <Link to={`${basePath}/envelopes/${entry.envelopeId}`} className="font-mono text-link hover:underline">
          {entry.envelopeId.slice(0, 8)}…
        </Link>
        {entry.exempt ? <Badge>Not billed</Badge> : null}
      </span>
    )
  }
  if (hasEntryDetail(entry)) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="text-left text-link hover:underline"
          onClick={(event) => {
            event.stopPropagation()
            onOpen()
          }}
        >
          {entryText(entry)}
        </button>
        <RefundBadge entry={entry} />
      </span>
    )
  }
  return <span>{entryText(entry)}</span>
}

interface BalanceCardProps {
  summary: BillingSummaryDto
  /** An Owner's buttons, when they can buy or refund. */
  actions: React.ReactNode
  /** Opens the low-balance alert setting, for an Owner. */
  onChangeAlert: (() => void) | null
}

/** Where the low-balance alert stands. An organization PixelSign doesn't bill gets no billing notices, so it has none. */
function AlertLine({ summary, onChangeAlert }: Omit<BalanceCardProps, 'actions'>) {
  if (summary.exempt) return null
  const threshold = summary.lowBalanceThresholdMicros
  return (
    <p className="flex flex-wrap items-center gap-2 text-body">
      {threshold > 0 ? `Low-balance alert below ${formatMicros(threshold)}` : 'Low-balance alert off'}
      {onChangeAlert ? (
        <Button variant="secondary" size="sm" onClick={onChangeAlert}>
          Change
        </Button>
      ) : null}
    </p>
  )
}

function BalanceCard({ summary, actions, onChangeAlert }: BalanceCardProps) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-end justify-between gap-4 pt-6">
        <div className="space-y-2">
          <p className="text-caption text-muted-foreground">Balance</p>
          <p className="text-heading font-semibold tabular-nums">{formatMicros(summary.balanceMicros)}</p>
          <p className="text-caption text-muted-foreground tabular-nums">
            {formatMicros(summary.grantedMicros)} granted · {formatMicros(summary.paidMicros)} paid ·{' '}
            {formatMicros(summary.refundableMicros)} refundable
          </p>
          <p className="flex flex-wrap items-center gap-2 text-body">
            Each live envelope costs {formatPrice(summary.priceMicros)}
            {summary.nextPriceChange ? (
              <Badge tone="info">
                Changes to {formatPrice(summary.nextPriceChange.priceMicros)} on{' '}
                {new Date(summary.nextPriceChange.effectiveAt).toLocaleDateString()}
              </Badge>
            ) : null}
          </p>
          <AlertLine summary={summary} onChangeAlert={onChangeAlert} />
        </div>
        {actions}
      </CardContent>
    </Card>
  )
}

/**
 * The organization's prepaid credit: its balance, the price of a live
 * envelope, and the history of charges and credit. Every member can read it;
 * Owners buy credit through Stripe Checkout and refund what they haven't spent.
 */
export function BillingPage() {
  const scope = useDashboardScope()
  const [params, setParams] = useSearchParams()
  // A hand-edited value that isn't a cursor or an id is ignored, the same way other pages drop a bad search param.
  const beforeParsed = pageCursorSchema.safeParse(params.get('before'))
  const before = beforeParsed.success ? beforeParsed.data : null
  const entryParsed = uuidParamSchema.safeParse(params.get('entry'))
  const selectedId = entryParsed.success ? entryParsed.data : null
  const purchaseParsed = uuidParamSchema.safeParse(params.get('purchase'))
  const purchaseId = purchaseParsed.success ? purchaseParsed.data : null
  const cardParsed = uuidParamSchema.safeParse(params.get('card'))
  const setupId = cardParsed.success ? cardParsed.data : null
  const summary = useBillingSummary()
  const history = useBillingHistory(before)
  const [dialog, setDialog] = React.useState<'buy' | 'refund' | 'threshold' | 'autoReload' | null>(null)
  const canManage = canManageBilling(scope)

  function page(next: string | null): void {
    const updated = new URLSearchParams(params)
    if (next) updated.set('before', next)
    else updated.delete('before')
    updated.delete('entry')
    setParams(updated)
  }

  const select = React.useCallback(
    (id: string | null, options: { replace: boolean } = { replace: false }) =>
      setParams((current) => {
        const next = new URLSearchParams(current)
        if (id) next.set('entry', id)
        else next.delete('entry')
        return next
      }, options),
    [setParams],
  )

  const clearCheckout = React.useCallback(
    () =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          next.delete('purchase')
          next.delete('cancelled')
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  const clearCardSetup = React.useCallback(
    () =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          next.delete('card')
          next.delete('cancelled')
          return next
        },
        { replace: true },
      ),
    [setParams],
  )

  function renderActions(data: BillingSummaryDto) {
    if (!canManage) return null
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" disabled={data.refundableMicros === 0} onClick={() => setDialog('refund')}>
          Refund
        </Button>
        {data.exempt ? null : <Button onClick={() => setDialog('buy')}>Buy credit</Button>}
      </div>
    )
  }

  function renderSummary() {
    if (summary.isLoading) return <LoadingState />
    if (summary.isError || !summary.data) {
      return <ErrorState message={apiErrorMessage(summary.error, 'Could not load billing.')} />
    }
    return (
      <div className="space-y-4">
        {summary.data.exempt ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BadgeCheck className="size-4 text-success" />
                Not billed
              </CardTitle>
              <CardDescription>
                PixelSign doesn&apos;t bill {scope.tenantName}. Live sends are free and listed below at $0.00.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : null}
        <BalanceCard
          summary={summary.data}
          actions={renderActions(summary.data)}
          onChangeAlert={canManage ? () => setDialog('threshold') : null}
        />
        <AutoReloadCard summary={summary.data} onEdit={canManage ? () => setDialog('autoReload') : null} />
        {canManage ? (
          <>
            <BuyCreditDialog
              open={dialog === 'buy'}
              onOpenChange={(open) => setDialog(open ? 'buy' : null)}
              priceMicros={summary.data.priceMicros}
            />
            <RefundDialog
              open={dialog === 'refund'}
              onOpenChange={(open) => setDialog(open ? 'refund' : null)}
              refundableMicros={summary.data.refundableMicros}
            />
            <ThresholdDialog
              open={dialog === 'threshold'}
              onOpenChange={(open) => setDialog(open ? 'threshold' : null)}
              thresholdMicros={summary.data.lowBalanceThresholdMicros}
            />
            <AutoReloadDialog open={dialog === 'autoReload'} onOpenChange={(open) => setDialog(open ? 'autoReload' : null)} summary={summary.data} />
          </>
        ) : null}
      </div>
    )
  }

  function renderHistory() {
    if (history.isLoading) return <LoadingState />
    if (history.isError || !history.data) {
      return <ErrorState message={apiErrorMessage(history.error, 'Could not load the billing history.')} />
    }
    if (history.data.entries.length === 0 && !before) {
      return (
        <EmptyState
          icon={Coins}
          title="No billing activity yet"
          description="Live envelope charges and credit you add will appear here."
        />
      )
    }
    const nextBefore = history.data.nextBefore
    const entries = history.data.entries
    const detailed = entries.filter(hasEntryDetail)
    return (
      <div className="space-y-3">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => {
                const opens = hasEntryDetail(entry)
                return (
                  <TableRow
                    key={entry.id}
                    data-record-id={entry.id}
                    className={cn(opens && 'cursor-pointer', entry.id === selectedId && 'bg-selected')}
                    onClick={opens ? () => select(entry.id) : undefined}
                  >
                    <TableCell className="text-caption tabular-nums whitespace-nowrap">
                      {new Date(entry.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <EntryDescription entry={entry} basePath={scope.basePath} onOpen={() => select(entry.id)} />
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${entry.amountMicros > 0 ? 'text-success' : ''}`}>
                      {entry.amountMicros > 0 ? `+${formatMicros(entry.amountMicros)}` : formatMicros(entry.amountMicros)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatMicros(entry.balanceAfterMicros)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end gap-2">
          {before ? (
            <Button variant="ghost" size="sm" onClick={() => page(null)}>
              Back to newest
            </Button>
          ) : null}
          {nextBefore ? (
            <Button variant="secondary" size="sm" onClick={() => page(nextBefore)}>
              Older entries
            </Button>
          ) : null}
        </div>
        <RecordSheet title="Billing entry" ids={detailed.map((entry) => entry.id)} selectedId={selectedId} onSelect={select}>
          {(id) => {
            const entry = detailed.find((candidate) => candidate.id === id)
            return entry ? (
              <BillingEntryDetail entry={entry} />
            ) : (
              <p className="p-card text-body text-muted-foreground">This entry isn&apos;t on this page of the history.</p>
            )
          }}
        </RecordSheet>
      </div>
    )
  }

  return (
    <>
      <PageHeader title="Billing" description="Prepaid credit for live envelopes." />
      <ReadOnlyBanner />
      <div className="space-y-8">
        {purchaseId && canManage ? (
          <CheckoutReturn
            key={purchaseId}
            purchaseId={purchaseId}
            cancelled={params.get('cancelled') === '1'}
            onDone={clearCheckout}
          />
        ) : null}
        {setupId && canManage ? (
          <CardSetupReturn key={setupId} setupId={setupId} cancelled={params.get('cancelled') === '1'} onDone={clearCardSetup} />
        ) : null}
        {renderSummary()}
        <section className="space-y-3">
          <h2 className="text-title font-semibold">History</h2>
          {renderHistory()}
        </section>
      </div>
    </>
  )
}
